import {
  Delete,
  ForwardToInbox,
  NoAccounts,
  SettingsBackupRestore,
  SupervisorAccount,
} from '@mui/icons-material';
import { Stack } from '@mui/material';
import { useState } from 'react';
import { useNotification } from '../../hooks/NotificationContext';
import {
  modifyUserAccountStatus,
  resendInvites,
  updateUserRole,
} from '../../modules/users';
import { roleOptions } from '../../static/common';
import ActionMenu from '../../ui/primitives/ActionMenu';
import ConfirmationModal from '../../ui/primitives/ConfirmationModal';
import CustomSelect from '../../ui/primitives/CustomSelect';

const UserActions = ({
  row,
  fetchCustomerList,
  currentUser,
  orgManagersCount = 0,
}) => {
  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [loading, setLoading] = useState(false);
  const { addNotification } = useNotification();
  const [selectedRole, setSelectedRole] = useState(row?.role || '');

  const openConfirm = (action) => {
    setConfirmAction(action);
    if (action === 'role') setSelectedRole(row?.role);
    setShowConfirm(true);
  };

  const closeConfirm = () => {
    setShowConfirm(false);
  };

  const handleSubmit = async () => {
    if (!confirmAction || !row?.id) return;

    setLoading(true);
    try {
      if (confirmAction === 'invite') {
        await resendInvites(row);
        addNotification('success', 'Invite sent successfully!');
      } else if (confirmAction === 'role') {
        if (!selectedRole || selectedRole === row.role) {
          addNotification('info', 'No changes made to the user role.');
        } else {
          await updateUserRole(selectedRole, row.id);
          addNotification('success', 'User role updated successfully!');
          fetchCustomerList();
        }
      } else {
        await modifyUserAccountStatus([row.id], confirmAction);
        const statusMsg = {
          delete: 'deleted permanently',
          deactivate: 'deactivated',
          restore: 'reactivated',
        };
        addNotification(
          'success',
          `User ${statusMsg[confirmAction]} successfully!`
        );
        fetchCustomerList();
      }
      closeConfirm();
    } catch (err) {
      addNotification('danger', err.message || 'Something went wrong.');
    }
    setLoading(false);
  };

  const isInviteDisabled = row?.status === 'invite-accepted';

  const confirmationTextMap = {
    invite: `Are you sure you want to resend the invite to ${row?.first_name}?`,
    role:
      row?.role === 'team-member'
        ? 'Are you sure you want to promote this user to organization manager?'
        : 'Are you sure you want to demote this user back to team member?',
    delete: `Are you sure you want to permanently delete ${row?.first_name}? This action cannot be undone.`,
    deactivate: `Are you sure you want to deactivate ${row?.first_name}?`,
    restore: `Are you sure you want to reactivate ${row?.first_name}?`,
  };

  const menuItems = [
    {
      label: 'Update Role',
      icon: <SupervisorAccount />,
      onClick: () => openConfirm('role'),
      hidden: row?.status !== 'active',
    },
    {
      label: 'Resend Invite',
      icon: <ForwardToInbox />,
      onClick: () => openConfirm('invite'),
      disabled: isInviteDisabled,
      hidden: row?.status !== 'invited',
    },
    {
      label: 'Delete Permanently',
      icon: <Delete />,
      onClick: () => openConfirm('delete'),
      color: 'error',
      hidden: !(
        currentUser?.role === 'org-admin' &&
        row?.role !== 'org-admin' &&
        row?.status !== 'active'
      ),
    },
    {
      label: 'Deactivate User',
      icon: <NoAccounts />,
      onClick: () => openConfirm('deactivate'),
      color: 'warning',
      hidden: !(
        currentUser?.role === 'org-admin' &&
        row?.role !== 'org-admin' &&
        row?.status === 'active'
      ),
    },
    {
      label: 'Reactivate User',
      icon: <SettingsBackupRestore />,
      onClick: () => openConfirm('restore'),
      color: 'success',
      hidden: !(
        currentUser?.role === 'org-admin' &&
        row?.role !== 'org-admin' &&
        row?.status === 'inactive'
      ),
    },
  ];

  const modalTitleMap = {
    invite: 'Resend Invite',
    role: 'Update Member Role',
    delete: 'Delete User',
    deactivate: 'Deactivate User',
    restore: 'Reactivate User',
  };

  return (
    <>
      <ActionMenu items={menuItems} tooltip="User actions" />
      <ConfirmationModal
        show={showConfirm}
        handleClose={closeConfirm}
        handleConfirm={handleSubmit}
        title={modalTitleMap[confirmAction] ?? ''}
        body={
          confirmAction === 'role' ? (
            <Stack spacing={2} sx={{ width: '100%' }}>
              <CustomSelect
                id="select-role"
                label="Select New Role"
                value={selectedRole}
                onChange={setSelectedRole}
                options={roleOptions.map((role) => ({
                  id: role.value,
                  name: role.label,
                }))}
                placeholder="Choose a role"
              />
            </Stack>
          ) : (
            confirmationTextMap[confirmAction]
          )
        }
        loading={loading}
      />
    </>
  );
};

export default UserActions;